import { type ElementType, type ReactNode, type ComponentPropsWithoutRef } from 'react'

const variantStyles = {
  primary: 'bg-blue-600 hover:bg-blue-700 text-white',
  secondary: 'bg-subtle-bg hover:bg-muted-bg text-primary',
  danger: 'bg-red-600 hover:bg-red-700 text-white',
  ghost: 'bg-transparent text-muted hover:bg-hover',
  orange: 'bg-orange-600 hover:bg-orange-700 text-white',
  purple: 'bg-purple-600 hover:bg-purple-700 text-white',
} as const

const sizeStyles = {
  sm: 'text-sm py-1 px-3',
  md: 'py-2 px-4',
  icon: 'p-2',
} as const

const base =
  'rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface'

type ButtonVariant = keyof typeof variantStyles
type ButtonSize = keyof typeof sizeStyles

type ButtonBaseProps = {
  variant?: ButtonVariant
  loading?: boolean
  icon?: ReactNode
  className?: string
  children?: ReactNode
}

// Discriminated union on size: icon buttons MUST supply an aria-label (or
// aria-labelledby) at the type level. For any other size aria-label is
// optional. Using a discriminant avoids sprinkling `!` or `as` at callsites
// and gives a helpful compile-time nudge when adding icon-only buttons.
type IconSizeProps =
  | (ButtonBaseProps & {
      size: 'icon'
      'aria-label': string
    })
  | (ButtonBaseProps & {
      size: 'icon'
      'aria-labelledby': string
    })

type NonIconSizeProps = ButtonBaseProps & {
  size?: Exclude<ButtonSize, 'icon'>
}

type ButtonOwnProps = IconSizeProps | NonIconSizeProps

type ButtonProps<C extends ElementType = 'button'> = ButtonOwnProps & {
  as?: C
} & Omit<ComponentPropsWithoutRef<C>, keyof ButtonBaseProps | 'as' | 'size'>

/** disabled / aria-disabled / aria-busy for the rendered element: native buttons get `disabled`, polymorphic `as` targets get aria-disabled. */
function stateAttributes(isNativeButton: boolean, disabled: boolean | undefined, loading: boolean | undefined) {
  const inactive = disabled || loading
  return {
    disabled: isNativeButton ? inactive : undefined,
    'aria-disabled': isNativeButton ? undefined : inactive || undefined,
    'aria-busy': loading || undefined,
  }
}

export function Button<C extends ElementType = 'button'>({
  as,
  variant = 'primary',
  size = 'md',
  loading,
  icon,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps<C>) {
  const Component = as || 'button'

  const classes = [base, variantStyles[variant], sizeStyles[size], className].filter(Boolean).join(' ')

  return (
    <Component className={classes} {...stateAttributes(Component === 'button', disabled, loading)} {...rest}>
      {loading ? (
        '\u2026'
      ) : (
        <>
          {icon}
          {children}
        </>
      )}
    </Component>
  )
}
